import * as React from 'react'
import { FormGroupProps, FormGroup } from '../FormGroup'
import { useControlled, useForkRef, useId } from '../../hooks'
import { RadioGroupContext } from './RadioGroupContext'

export interface RadioGroupProps extends Omit<FormGroupProps, 'onChange'> {
  /**
   * The default value. Use when the component is not controlled.
   */
  defaultValue?: any
  /**
   * The name used to reference the value of the control.
   * If you don't provide this prop, it falls back to a randomly generated name.
   */
  name?: string
  /**
   * Callback fired when a radio button is selected.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * @param {string} value The value of the selected radio button.
   * You can pull out the new value by accessing `event.target.value` (string).
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void
  /**
   * Value of the selected radio button. The DOM API casts this to a string.
   */
  value?: any
  actions?: any
}

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  function RadioGroup(props, ref) {
    const {
      // private
      // eslint-disable-next-line react/prop-types
      actions,
      children,
      defaultValue,
      name: nameProp,
      onChange,
      value: valueProp,
      ...other
    } = props
    const rootRef = React.useRef<HTMLDivElement>(null)

    const [value, setValueState] = useControlled({
      controlled: valueProp,
      default: defaultValue,
      name: 'RadioGroup',
    })

    React.useImperativeHandle(
      actions,
      () => ({
        focus: () => {
          let input = rootRef.current?.querySelector(
            'input:not(:disabled):checked'
          )

          if (!input) {
            input = rootRef.current?.querySelector('input:not(:disabled)')
          }

          if (input) {
            // @ts-ignore
            input?.focus()
          }
        },
      }),
      []
    )

    const handleRef = useForkRef(ref, rootRef)

    const name = useId(nameProp)

    const contextValue = React.useMemo(
      () => ({
        name,
        onChange(event: any) {
          setValueState(event.target.value)

          if (onChange) {
            onChange(event, event.target.value)
          }
        },
        value,
      }),
      [name, onChange, setValueState, value]
    )

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <FormGroup role="radiogroup" ref={handleRef} {...other}>
          {children}
        </FormGroup>
      </RadioGroupContext.Provider>
    )
  }
)
